import { Manager, Notifications } from "@twilio/flex-ui";
import uhaulStore from "../../../../CustomRedux/UhaulStore";
import ExceptionLogger from "../../../../ExceptionHelper";
import { NotificationIds } from "../flex-hooks/notifications/TransferResult";
import parkingState from "../helpers/parkingState";
import { ParkChatActionPayload } from "../types/ActionPayloads";

export const name = "ParkChat";
// const URL_PARK_AN_INTERACTION = process.env.FLEX_APP_URL_PARK_AN_INTERACTION;

export const getAgents = async (payload: any) => {
  const { flexInteractionChannelSid } = payload.task.attributes;

  ExceptionLogger(null, "getAgents", "ParkChat - fetching participants", "", {
    taskSid: payload.task.taskSid,
    flexInteractionChannelSid
  });

  const participants = await payload.task.getParticipants(flexInteractionChannelSid);

  ExceptionLogger(null, "getAgents", "ParkChat - participants result", "", {
    taskSid: payload.task.taskSid,
    participantsCount: participants?.length ?? 0,
    participantTypes: participants?.map((x: any) => x.type) ?? []
  });

  let agents = [];
  if (participants !== null && participants.length > 0) {
    agents = participants.filter((x: any) => x.type === "agent");
  }

  return agents;
}

export const handle = async (payload: ParkChatActionPayload) => {
  try {
    const agents = await getAgents(payload);
    if (agents !== null && agents.length > 0) {
      const agent = agents[0];

      if (!agent.mediaProperties || !agent.mediaProperties.conversationSid) {
        ExceptionLogger(
          new Error("ParkChat - agent.mediaProperties or conversationSid is missing"),
          "handle",
          "ParkChat failed - agent participant returned from Twilio is missing mediaProperties.conversationSid.",
          "",
          {
            taskSid: payload.task.taskSid,
            agentParticipantSid: agent.participantSid,
            agentInteractionSid: agent.interactionSid,
            mediaProperties: agent.mediaProperties ?? null
          }
        );
        return Notifications.showNotification(NotificationIds.ParkChatFailed);
      }

      const participantsSid: any = [];
      agents.forEach((x: any) => participantsSid.push(x.participantSid));
      const manager = Manager.getInstance();
      const { conversationSid } = agent.mediaProperties;
      const body = {
        channelSid: agent.channelSid,
        interactionSid: agent.interactionSid,
        participantSid: agent.participantSid,
        participantsSid,
        conversationSid,
        taskSid: payload.task.taskSid,
        workflowSid: payload.task.workflowSid,
        taskChannelUniqueName: payload.task.taskChannelUniqueName,
        // targetSid: payload.task.targetSid,
        workerName: manager.user.identity,
        taskAttributes: payload.task.attributes
      };
      const serviceUrl: string = uhaulStore.getState().UhaulReducer.currentUser.serviceBaseUrl;

      const missingFields = [
        ["channelSid", body.channelSid],
        ["interactionSid", body.interactionSid],
        ["participantSid", body.participantSid],
        ["conversationSid", body.conversationSid],
        ["taskSid", body.taskSid],
        ["workflowSid", body.workflowSid],
        ["taskChannelUniqueName", body.taskChannelUniqueName],
        ["workerName", body.workerName],
        ["taskAttributes", body.taskAttributes],
        ["serviceUrl", serviceUrl],
      ].filter(([, value]) => value === null || value === undefined || value === "");

      if (missingFields.length > 0) {
        ExceptionLogger(
          new Error(`ParkChat - missing required fields before fetch: ${missingFields.map(([name]) => name).join(", ")}`),
          "handle",
          "ParkChat - one or more body fields were null/undefined before calling park-an-interaction.",
          "",
          { taskSid: payload.task.taskSid, missingFields: missingFields.map(([name]) => name) }
        );
      }

      parkingState.markConversationAsParking(conversationSid);
      try {
        await fetch(`${serviceUrl}park-an-interaction`, {
          headers: {
            "Content-Type": "application/json"
          },
          method: "POST",
          body: JSON.stringify(body)
        })
        .then(async () => {
          const conversation = await manager.conversationsClient.getConversationBySid(
            conversationSid
          );
          const foundAttributes = await conversation.getAttributes();

          if (!foundAttributes || !(foundAttributes as any).webhookSid){
            throw new Error(`Cannot find attributes or the new webhook from conversationid, ${conversationSid}`);
          }
        });
      } finally {
        parkingState.clearConversationParking(conversationSid);
      }
        
      return Notifications.showNotification(NotificationIds.ParkChatSuccess);
    }

    if (payload.task.attributes.flexInteractionChannelSid) {
      ExceptionLogger(
        new Error("ParkChat - participants found but none have type 'agent'"),
        "handle",
        "ParkChat failed - getParticipants returned results but no agent-typed participants. Agent may have already been removed from the interaction.",
        "",
        {
          taskSid: payload.task.taskSid,
          taskChannelUniqueName: payload.task.taskChannelUniqueName,
          flexInteractionChannelSid: payload.task.attributes.flexInteractionChannelSid,
          taskStatus: payload.task.taskStatus
        }
      );
    } else {
      ExceptionLogger(
        new Error("ParkChat - flexInteractionChannelSid is missing from task attributes"),
        "handle",
        "ParkChat failed - flexInteractionChannelSid was null or undefined.",
        "",
        {
          taskSid: payload.task.taskSid,
          taskChannelUniqueName: payload.task.taskChannelUniqueName,
          taskStatus: payload.task.taskStatus,
          taskAttributes: payload.task.attributes
        }
      );
    }
    return Notifications.showNotification(NotificationIds.ParkChatFailed);
  } 
  catch (error) {
    console.error(error);
    ExceptionLogger(error, "handle", "ParkChat.ts", "", payload);
    return Notifications.showNotification(NotificationIds.ParkChatFailed);
  }
}